import { useMemo, useState } from 'react';

// Renders a ticket message body. Two jobs, both at render time so the
// stored message stays exactly what arrived (it is the record of what the
// client sent) and tickets already in the database are fixed too:
//
// 1. Quoted history goes behind a "Show quoted text" toggle. A client
//    replying from Outlook or Gmail sends the whole conversation back
//    underneath their reply, so every message repeated every earlier one.
//    The cut point is the reply header ("From: ... Sent: ... Subject:"),
//    Gmail's quote block, "On <date> ... wrote:" or "-----Original
//    Message-----". Nothing is removed: it is one click away.
// 2. Email HTML is rendered as HTML. The pre-wrap the thread uses for
//    typed replies turned every newline in Outlook's markup into a blank
//    line. Runs of empty paragraphs and <br>s are collapsed to one, and
//    images that can never load are dropped (cid: signature images lose
//    their src in the sanitizer, and the CSP only allows img-src 'self').
//
// Plain-text bodies (replies typed in the app) are left alone and keep
// pre-wrap, which is what gives them their line breaks.

export interface FormattedEmailBody {
  isHtml: boolean;
  mainHtml: string;
  quotedHtml: string | null;
}

const HTML_MARKUP = /<(p|div|br|ul|ol|li|h[1-6]|blockquote|pre|span|strong|em|a)\b/i;
const BLANK_TEXT = /^[\s\u00a0\u200b\u200c\ufeff]*$/;
const SEPARATOR_TEXT = /^[\s\u00a0]*[_\-=*]{5,}[\s\u00a0]*$/;
// No \b before Sent:/Subject: - the <br> between header lines has no
// text, so the address runs straight into "Sent:".
const REPLY_HEADER = /^\s*From:[\s\S]{1,400}?(Sent|Date):[\s\S]{1,400}?Subject:/i;
const ORIGINAL_MESSAGE = /^\s*-{2,}\s*(Original|Forwarded) Message\s*-{2,}/i;
const ON_WROTE = /^\s*On\s[\s\S]{4,300}?\swrote:/i;
const QUOTE_SELECTOR = '.gmail_quote, .gmail_quote_container, .yahoo_quoted, .moz-cite-prefix';

export function formatEmailBody(html: string): FormattedEmailBody {
  if (!html || !HTML_MARKUP.test(html)) {
    return { isHtml: false, mainHtml: html, quotedHtml: null };
  }
  const root = parse(html);
  const cut = findQuoteStart(root);
  if (cut) {
    const range = root.ownerDocument.createRange();
    range.setStartBefore(cut);
    range.setEndAfter(root.lastChild!);
    const quoted = root.ownerDocument.createElement('div');
    quoted.appendChild(range.extractContents());
    // A message that is nothing but quoted history (an empty reply, a
    // bare forward) is shown whole rather than as an empty card.
    if (hasContent(root)) {
      return { isHtml: true, mainHtml: tidy(root), quotedHtml: tidy(quoted) };
    }
  }
  return { isHtml: true, mainHtml: tidy(parse(html)), quotedHtml: null };
}

/** Plain-text preview of the part of a message the client actually wrote. */
export function emailPreview(html: string, maxChars = 120): string {
  const { isHtml, mainHtml } = formatEmailBody(html);
  const text = isHtml
    ? (parse(mainHtml).textContent ?? '')
    : mainHtml.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ');
  const flat = text.replace(/\s+/g, ' ').trim();
  if (!flat) return '(empty message)';
  return flat.length > maxChars ? flat.slice(0, maxChars).trimEnd() + '…' : flat;
}

export function EmailBody({ html }: { html: string }): React.ReactElement {
  const body = useMemo(() => formatEmailBody(html), [html]);
  const [showQuoted, setShowQuoted] = useState(false);

  if (!body.isHtml) {
    return (
      <div
        data-testid="email-body"
        className="whitespace-pre-wrap break-words text-sm text-[var(--color-body)]"
        dangerouslySetInnerHTML={{ __html: body.mainHtml }}
      />
    );
  }
  return (
    <div data-testid="email-body" className="text-sm text-[var(--color-body)]">
      <div
        className="break-words [&_img]:max-w-full"
        dangerouslySetInnerHTML={{ __html: body.mainHtml }}
      />
      {body.quotedHtml && (
        <>
          <button
            type="button"
            onClick={() => setShowQuoted((v) => !v)}
            aria-expanded={showQuoted}
            className="mt-2 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-0.5 text-xs text-[var(--color-muted)] hover:bg-[var(--color-surface)]"
          >
            {showQuoted ? 'Hide quoted text' : '··· Show quoted text'}
          </button>
          {showQuoted && (
            <div
              data-testid="email-quoted"
              className="mt-2 break-words border-l-2 border-[var(--color-border)] pl-3 text-[var(--color-muted)] [&_img]:max-w-full"
              dangerouslySetInnerHTML={{ __html: body.quotedHtml }}
            />
          )}
        </>
      )}
    </div>
  );
}

function parse(html: string): HTMLElement {
  // DOMParser documents are inert: no scripts run, no images load.
  return new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html').body;
}

function findQuoteStart(root: HTMLElement): Node | null {
  const candidates: Node[] = [];
  const marked = root.querySelector(QUOTE_SELECTOR);
  if (marked) candidates.push(marked);

  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let t = walker.nextNode(); t; t = walker.nextNode()) {
    const text = t.nodeValue ?? '';
    if (BLANK_TEXT.test(text)) continue;
    const lead = text.trimStart();
    let hit = false;
    if (/^From:/i.test(lead)) hit = REPLY_HEADER.test(textFrom(root, t, 900));
    else if (/^-{2,}/.test(lead)) hit = ORIGINAL_MESSAGE.test(lead);
    else if (/^On\s/i.test(lead)) {
      // "On <date>, <name> wrote:" - the date is what separates it from
      // a client sentence that happens to start with "On".
      const m = ON_WROTE.exec(textFrom(root, t, 400));
      hit = Boolean(m && /\d/.test(m[0]));
    }
    if (hit) {
      candidates.push(climb(t, root));
      break;
    }
  }
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING ? b : a,
  );
}

function textFrom(root: HTMLElement, node: Node, maxChars: number): string {
  const range = root.ownerDocument.createRange();
  range.setStartBefore(node);
  range.setEndAfter(root.lastChild!);
  return range.toString().slice(0, maxChars);
}

// Cut at the outermost element the header text opens, so the header's
// wrapper (and its <strong>From:</strong>) goes with it - but never past
// an element that holds something before the header.
function climb(node: Node, root: HTMLElement): Node {
  let n = node;
  while (n.parentNode && n.parentNode !== root && nothingBefore(n)) n = n.parentNode;
  return n;
}

function nothingBefore(node: Node): boolean {
  for (let s = node.previousSibling; s; s = s.previousSibling) {
    if (!isBlank(s, true)) return false;
  }
  return true;
}

function hasContent(el: HTMLElement): boolean {
  return !BLANK_TEXT.test(el.textContent ?? '') || el.querySelector('img') !== null;
}

function isBlank(node: Node, separatorsToo = false): boolean {
  if (node.nodeType === Node.TEXT_NODE) {
    const v = node.nodeValue ?? '';
    return BLANK_TEXT.test(v) || (separatorsToo && SEPARATOR_TEXT.test(v));
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return true;
  const el = node as HTMLElement;
  if (el.tagName === 'BR') return true;
  if (el.tagName === 'IMG' || el.querySelector('img')) return false;
  const text = el.textContent ?? '';
  return BLANK_TEXT.test(text) || (separatorsToo && SEPARATOR_TEXT.test(text));
}

function tidy(root: HTMLElement): string {
  for (const img of Array.from(root.querySelectorAll('img'))) {
    if (!canLoad(img.getAttribute('src'))) img.remove();
  }
  // One empty paragraph is a paragraph break; a run of them is the gap.
  for (const el of Array.from(root.querySelectorAll('p, div'))) {
    if (!el.isConnected || !isBlank(el)) continue;
    const prev = previousNonWhitespace(el);
    if (prev && prev.nodeType === Node.ELEMENT_NODE && isBlank(prev) && prev.nodeName !== 'BR') {
      el.remove();
    }
  }
  // Same for line breaks: at most one blank line in a row.
  for (const br of Array.from(root.querySelectorAll('br'))) {
    const p1 = previousNonWhitespace(br);
    const p2 = p1 && previousNonWhitespace(p1);
    if (p1?.nodeName === 'BR' && p2?.nodeName === 'BR') br.remove();
  }
  trimEdge(root, 'start');
  trimEdge(root, 'end');
  return root.innerHTML;
}

function previousNonWhitespace(node: Node): Node | null {
  let s = node.previousSibling;
  while (s && s.nodeType === Node.TEXT_NODE && BLANK_TEXT.test(s.nodeValue ?? '')) s = s.previousSibling;
  return s;
}

// Leading and trailing blank lines, and the "______" rule Outlook puts
// above the reply header, which is left dangling once the header is cut.
function trimEdge(el: Node, edge: 'start' | 'end'): void {
  for (;;) {
    const c = edge === 'start' ? el.firstChild : el.lastChild;
    if (!c) return;
    if (isBlank(c, true)) {
      c.parentNode!.removeChild(c);
      continue;
    }
    if (c.nodeType === Node.ELEMENT_NODE) trimEdge(c, edge);
    return;
  }
}

function canLoad(src: string | null): boolean {
  if (!src) return false;
  if (src.startsWith('data:image/')) return true;
  try {
    return new URL(src, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}
